import React, { useEffect, useState } from "react";
import { ACTIVATABLE_MODULES, ACTIVATABLE_MODULE_KEYS, type ActivatableModuleKey } from "@redipos/contracts/modules";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Ban, CheckCircle2, CreditCard, ExternalLink, Mail, ChefHat, Receipt, Wrench, FileSignature, Scale, Wallet, IdCard } from "lucide-react";
import { Badge, Button, DetailPage, Section } from "@redipos/ui";
import type { ConsoleEmailConfigApi, ConsoleTenantDetail, ConsoleTenantHealthRow } from "@redipos/contracts";
import { operacionesApi } from "../api.js";
import { plataformaApi } from "../../plataforma/api.js";
import { APP_PUBLIC_URL } from "../../../constants.js";
import { CATEGORIA_BADGE, etapaInfo } from "../lib/activationStages.js";

function errMsg(e: unknown, fallback = "Error") {
  return e instanceof Error ? e.message : fallback;
}

function formatDate(value: unknown) {
  if (!value) return "—";
  return new Date(value as string | number | Date).toLocaleDateString("es-PY");
}

function formatDateTime(value: unknown) {
  if (!value) return "—";
  return new Date(value as string | number | Date).toLocaleString("es-PY", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Módulos activables por tenant vía `apps/api/src/lib/moduleAccess.ts`
 * (`ACTIVATABLE_MODULES`). Lista corta hardcodeada acá a propósito — es solo
 * para el panel interno, no vale la pena compartir el tipo con el backend.
 */
/** Íconos y notas propias del ERP; etiqueta y descripción vienen del registro compartido. */
const MODULE_UI: Record<ActivatableModuleKey, { icon: typeof ChefHat; bootstrapNote?: string }> = {
  gastronomia: { icon: ChefHat, bootstrapNote: " Se crearon Cocina, Barra y 10 mesas iniciales." },
  facturacion_electronica: { icon: Receipt },
  servicios: { icon: Wrench },
  contratos: { icon: FileSignature },
  balanza: { icon: Scale },
  cuentas: { icon: Wallet },
  rrhh: { icon: IdCard },
};

const TENANT_MODULES = ACTIVATABLE_MODULE_KEYS.map((key) => ({
  key,
  label: ACTIVATABLE_MODULES[key].label,
  description: ACTIVATABLE_MODULES[key].description,
  ...MODULE_UI[key],
}));

/**
 * Ficha de cliente — Operaciones/Customer Success. La contraparte de
 * facturación (plan, suscripción, pagos) vive en el módulo Finanzas
 * (`/clientes/:id/facturacion`), separada a propósito: ver
 * docs/analisis/consola-admin-rediseno.md §4.1.
 */
export default function TenantDetailPage() {
  const { id } = useParams();
  const [tenant, setTenant] = useState<ConsoleTenantDetail | null>(null);
  const [health, setHealth] = useState<ConsoleTenantHealthRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [emailCfg, setEmailCfg] = useState<ConsoleEmailConfigApi | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [moduleStatus, setModuleStatusState] = useState<Record<string, boolean>>({});
  const [savingModule, setSavingModule] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    const tid = id;
    if (!tid) {
      setLoading(false);
      setErr("Falta el identificador del cliente.");
      return;
    }
    try {
      const [t, eCfg, h, ...modules] = await Promise.all([
        operacionesApi.tenant(tid),
        plataformaApi.emailConfig(),
        operacionesApi.tenantHealthOne(tid).catch(() => null),
        ...TENANT_MODULES.map((m) => operacionesApi.moduleStatus(tid, m.key)),
      ]);
      setTenant(t);
      setEmailCfg(eCfg);
      setHealth(h);
      setModuleStatusState(
        Object.fromEntries(TENANT_MODULES.map((m, i) => [m.key, modules[i].enabled]))
      );
    } catch (e) {
      setErr(errMsg(e, "Error"));
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = async (moduleKey: string) => {
    if (!tenant) return;
    const def = TENANT_MODULES.find((m) => m.key === moduleKey);
    const nextEnabled = !moduleStatus[moduleKey];
    setSavingModule(moduleKey); setErr(null);
    try {
      const result = await operacionesApi.setModuleStatus(tenant.id, moduleKey, nextEnabled);
      setModuleStatusState((prev) => ({ ...prev, [moduleKey]: result.enabled }));
      setOkMsg(
        result.enabled
          ? `${def?.label ?? moduleKey} activado.${def?.bootstrapNote ?? ""}`
          : `${def?.label ?? moduleKey} desactivado. Los datos se conservaron.`
      );
    } catch (e) { setErr(errMsg(e)); }
    finally { setSavingModule(null); }
  };

  useEffect(() => {
    load();
  }, [id]);

  const toggleActive = async () => {
    if (!tenant) return;
    try {
      await operacionesApi.updateTenant(tenant.id, { isActive: !tenant.isActive });
      await load();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const openAsClient = async () => {
    if (!tenant) return;
    setErr(null);
    try {
      const r = await operacionesApi.impersonate(tenant.id);
      const url = `${APP_PUBLIC_URL.replace(/\/$/, "")}/auth/handoff#token=${encodeURIComponent(r.token)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e2) {
      setErr(errMsg(e2));
    }
  };

  const resendVerification = async (userId: string) => {
    if (!tenant) return;
    setResendingId(userId);
    setErr(null);
    setOkMsg(null);
    try {
      const r = await operacionesApi.resendUserVerification(tenant.id, userId);
      if (r.alreadyVerified) {
        setOkMsg("Ese correo ya estaba verificado.");
      } else {
        setOkMsg(
          "Solicitud registrada: el correo de confirmación se encoló. Revisá la bandeja (y en Plataforma > Correo > Eventos si el envío falla).",
        );
      }
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setResendingId(null);
    }
  };

  if (loading && !tenant) {
    return <p className="text-fg-subtle text-sm py-12 text-center">Cargando…</p>;
  }
  if (err && !tenant) {
    return (
      <div className="space-y-4">
        <p className="text-danger">{err}</p>
        <Link to="/clientes" className="text-brand-500 text-sm">
          ← Volver
        </Link>
      </div>
    );
  }
  if (!tenant) return null;

  const metrics = [
    { key: "estado", label: "Estado plataforma", value: tenant.isActive === false ? "Suspendida" : "Activa" },
    { key: "registro", label: "Registro", value: formatDateTime(tenant.createdAt) },
    { key: "productos", label: "Productos", value: tenant.stats.productos.toLocaleString("es-PY") },
    {
      key: "ventas",
      label: "Ventas",
      value: tenant.stats.comprobantes.toLocaleString("es-PY"),
      hint: `${tenant.stats.ventas.toLocaleString("es-PY")} líneas`,
    },
    { key: "clientes", label: "Clientes", value: tenant.stats.clientes.toLocaleString("es-PY") },
    { key: "usuarios", label: "Usuarios", value: tenant.stats.usuarios.toLocaleString("es-PY") },
  ];

  return (
    <DetailPage
      title={tenant.name}
      subtitle={tenant.id}
      metrics={metrics}
      actions={
        <div className="flex flex-wrap gap-2">
          <Link to={`/clientes/${tenant.id}/facturacion`}>
            <Button type="button" variant="secondary">
              <CreditCard className="w-4 h-4" />
              Ver facturación
            </Button>
          </Link>
          <Button type="button" variant="secondary" onClick={openAsClient}>
            <ExternalLink className="w-4 h-4" />
            Abrir panel del cliente
          </Button>
          <Button
            type="button"
            variant={tenant.isActive === false ? "primary" : "danger"}
            onClick={toggleActive}
          >
            {tenant.isActive === false ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Activar cuenta
              </>
            ) : (
              <>
                <Ban className="w-4 h-4" />
                Suspender cuenta
              </>
            )}
          </Button>
        </div>
      }
      shellSpacing="lg"
    >
      <Link to="/clientes" className="inline-flex items-center gap-1 text-sm text-fg-subtle hover:text-fg-muted">
        <ArrowLeft className="w-4 h-4" />
        Clientes
      </Link>

      {err && (
        <p className="text-sm text-danger rounded-input border border-danger/40 bg-danger-soft px-3 py-2">{err}</p>
      )}
      {okMsg && (
        <p className="text-sm text-fg rounded-input border border-border bg-surface-elevated px-3 py-2">{okMsg}</p>
      )}

      <Section title="Organización" className="space-y-4">
        <dl className="text-sm space-y-2">
          <div className="flex justify-between gap-4">
            <dt className="text-fg-subtle">Registro</dt>
            <dd className="text-fg text-right">{formatDateTime(tenant.createdAt)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-fg-subtle">Onboarding</dt>
            <dd className="text-fg text-right">
              {tenant.onboardingCompletedAt ? formatDate(tenant.onboardingCompletedAt) : "Pendiente"}
            </dd>
          </div>
          {health ? (
            <>
              <div className="flex justify-between gap-4 items-center">
                <dt className="text-fg-subtle">Categoría</dt>
                <dd>
                  <Badge variant={CATEGORIA_BADGE[health.categoria] ?? "muted"} size="sm">
                    {health.categoria}
                  </Badge>
                </dd>
              </div>
              <div className="flex justify-between gap-4 items-center">
                <dt className="text-fg-subtle">Etapa de activación</dt>
                <dd>
                  <Badge variant={etapaInfo(health.etapa).variant} size="sm">
                    {etapaInfo(health.etapa).label}
                  </Badge>
                </dd>
              </div>
              {health.nota ? (
                <div className="flex justify-between gap-4">
                  <dt className="text-fg-subtle">Nota</dt>
                  <dd className="text-fg-subtle text-right text-xs max-w-[220px]">{health.nota}</dd>
                </div>
              ) : null}
            </>
          ) : null}
          {tenant.ruc ? (
            <div className="flex justify-between gap-4">
              <dt className="text-fg-subtle">RUC</dt>
              <dd className="text-fg">{tenant.ruc}</dd>
            </div>
          ) : null}
          {tenant.ciudad ? (
            <div className="flex justify-between gap-4">
              <dt className="text-fg-subtle">Ciudad</dt>
              <dd className="text-fg">{tenant.ciudad}</dd>
            </div>
          ) : null}
          {tenant.telefono ? (
            <div className="flex justify-between gap-4">
              <dt className="text-fg-subtle">Teléfono</dt>
              <dd className="text-fg">{tenant.telefono}</dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-4">
            <dt className="text-fg-subtle">Slug</dt>
            <dd className="text-fg">{tenant.slug || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4 items-center">
            <dt className="text-fg-subtle">Estado plataforma</dt>
            <dd>
              {tenant.isActive === false ? (
                <Badge variant="danger">Suspendida</Badge>
              ) : (
                <Badge variant="success">Activa</Badge>
              )}
            </dd>
          </div>
        </dl>
      </Section>

      <Section title="Módulos del comercio">
        <div className="flex flex-col gap-3">
          {TENANT_MODULES.map((m) => {
            const Icon = m.icon;
            const enabled = moduleStatus[m.key] === true;
            return (
              <div
                key={m.key}
                className="flex flex-col gap-4 rounded-input border border-border bg-surface-elevated p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-input bg-warning/15 text-warning">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-semibold text-fg">{m.label}</p>
                    <p className="mt-1 max-w-xl text-xs text-fg-subtle">
                      {m.description} También debe estar incluido en el plan asignado.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant={enabled ? "danger" : "primary"}
                  loading={savingModule === m.key}
                  loadingLabel="Guardando…"
                  onClick={() => toggleModule(m.key)}
                >
                  {enabled ? "Desactivar" : `Activar ${m.label}`}
                </Button>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Usuarios del tenant">
        <p className="text-xs text-fg-subtle mb-3">
          Reenviá el enlace de verificación al correo del usuario. Si el correo ya está verificado, no se envía nada
          nuevo.
        </p>
        {emailCfg && emailCfg.resendApiKeyConfigured === false && (
          <p className="text-xs text-warning mb-3 rounded-input border border-warning/50 bg-warning/10 px-3 py-2">
            No hay API key de Resend guardada en <strong>Plataforma &gt; Correo &gt; Configuración</strong>.
            Configurala ahí para que los reenvíos y la cola de correo puedan autenticarse con Resend.
          </p>
        )}
        <div className="rounded-input border border-border overflow-hidden">
          <div className="table-x-scroll max-w-full">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-subtle text-xs uppercase bg-surface-elevated">
                  <th className="px-3 py-2 font-medium">Usuario</th>
                  <th className="px-3 py-2 font-medium">Correo</th>
                  <th className="px-3 py-2 font-medium">Rol</th>
                  <th className="px-3 py-2 font-medium">Verificado</th>
                  <th className="px-3 py-2 font-medium w-0">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(!tenant.users || tenant.users.length === 0) && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-fg-subtle">
                      Sin usuarios
                    </td>
                  </tr>
                )}
                {tenant.users?.map((u: any) => (
                  <tr key={u.id} className="text-fg-muted">
                    <td className="px-3 py-2 text-fg">{u.usuario}</td>
                    <td className="px-3 py-2 break-all max-w-[220px]">{u.email}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{u.rol}</td>
                    <td className="px-3 py-2">
                      {u.emailVerifiedAt ? (
                        <Badge variant="success">Sí</Badge>
                      ) : (
                        <Badge variant="warning">Pendiente</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Button
                        type="button"
                        variant="secondary"
                        className="!px-2.5 !py-1.5 text-xs"
                        disabled={Boolean(u.emailVerifiedAt) || resendingId === u.id}
                        loading={resendingId === u.id}
                        loadingLabel="Enviando…"
                        onClick={() => resendVerification(u.id)}
                        title="Reenviar confirmación de cuenta"
                      >
                        <Mail className="w-4 h-4" />
                        Reenviar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>
    </DetailPage>
  );
}
